/**
 * Centralized toast notification utilities.
 * Provides standardized toast methods that integrate with the error handler.
 */

import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../constants/messages';
import Logger from './logger';

/**
 * Show an error toast with a user-friendly message.
 * Automatically extracts message from various error types.
 * Logs the full error for debugging while showing a safe message to users.
 *
 * @param error - The error object (Axios error, Error, string, or unknown)
 * @param fallbackMessage - Optional fallback message if extraction fails
 * @param logContext - Optional context for logging (e.g., operation name)
 */
export function showErrorToast(
  error: unknown,
  fallbackMessage?: string,
  logContext?: string
): void {
  // Log the full error for debugging (Logger handles production sanitization)
  if (logContext) {
    Logger.error(`${logContext}:`, error);
  } else {
    Logger.error('Error occurred:', error);
  }

  // Extract user-friendly message.
  // Le fallback DOIT passer en 2e argument : getApiErrorMessage renvoie toujours
  // une string non vide (GENERIC_ERROR par defaut), donc un `|| fallbackMessage`
  // en aval ne s'appliquait jamais — le message metier etait mort.
  const message = getApiErrorMessage(error, fallbackMessage || 'Une erreur est survenue');
  toast.error(message);
}

/**
 * Show a success toast notification.
 *
 * @param message - The success message to display
 */
export function showSuccessToast(message: string): void {
  toast.success(message);
}

/**
 * Show a loading toast notification.
 * Returns the toast ID which can be used to dismiss or update the toast.
 *
 * @param message - The loading message to display
 * @returns The toast ID for later dismissal
 */
export function showLoadingToast(message: string): string {
  return toast.loading(message);
}

/**
 * Dismiss a specific toast by ID.
 *
 * @param toastId - The ID of the toast to dismiss
 */
export function dismissToast(toastId: string): void {
  toast.dismiss(toastId);
}

/**
 * Dismiss all active toasts.
 */
export function dismissAllToasts(): void {
  toast.dismiss();
}

/**
 * Show an info toast notification.
 *
 * @param message - The info message to display
 */
export function showInfoToast(message: string): void {
  toast(message, {
    icon: 'i',
    style: {
      background: '#3B82F6',
      color: '#fff',
    },
  });
}

/**
 * Show a warning toast notification.
 *
 * @param message - The warning message to display
 */
export function showWarningToast(message: string): void {
  toast(message, {
    icon: '!',
    style: {
      background: '#F59E0B',
      color: '#fff',
    },
  });
}

/**
 * Update an existing toast (useful for loading -> success/error transitions).
 *
 * @param toastId - The ID of the toast to update
 * @param message - The new message
 * @param type - The toast type ('success' | 'error' | 'loading')
 */
export function updateToast(
  toastId: string,
  message: string,
  type: 'success' | 'error' | 'loading'
): void {
  toast.dismiss(toastId);

  switch (type) {
    case 'success':
      toast.success(message);
      break;
    case 'error':
      toast.error(message);
      break;
    case 'loading':
      toast.loading(message);
      break;
  }
}

/**
 * Promise-based toast that shows loading, then success/error.
 * Useful for async operations.
 *
 * @param promise - The promise to track
 * @param messages - Object containing loading, success, and error messages
 * @returns The promise result
 */
export async function toastPromise<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((err: unknown) => string);
  }
): Promise<T> {
  return toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: (err) => {
      Logger.error('Promise toast error:', err);
      if (typeof messages.error === 'function') {
        return messages.error(err);
      }
      // Use centralized error message extraction (meme piege du fallback mort
      // que dans showErrorToast : le fallback se passe en 2e argument).
      return getApiErrorMessage(err, messages.error);
    },
  });
}
